/**
 * Typed selectors and selector hooks for components.
 *
 * Object-returning selectors are paired with hooks that subscribe through
 * `useShallow`, so components re-render only when a selected field changes.
 * Plain `select*` functions also work with `useAppStore.getState()` inside
 * event handlers, where no subscription is needed.
 */

import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  deriveBlunderVerdict,
  type BlunderVerdict,
} from '@/logic/adjustments/blunderVerdict';
import type { ReportInput } from '@/utils/reportInput';
import type { AppStore } from './appStore';
import { useAppStore } from './appStore';

/** Everything the analysis log and the printed report need. */
export const selectReportInput = (state: AppStore): ReportInput => ({
  solutionStale: state.solutionStale,
  combinatorialStale: state.combinatorialStale,
  dataSnoopingStale: state.dataSnoopingStale,
  transformedStale: state.transformedStale,
  inputRevision: state.inputRevision,
  solutionRevision: state.solutionRevision,
  solutionInputRevision: state.solutionInputRevision,
  appliedScale: state.appliedScale,
  units: state.units,
  transformedFromRevision: state.transformedFromRevision,
  solution: state.solution,
  solutionTimestamp: state.solutionTimestamp,
  pointPairs: state.pointPairs,
  blunderResults: state.blunderResults,
  dataSnoopingResults: state.dataSnoopingResults,
  transformedPoints: state.transformedPoints,
});

/** Subscribe to the report input; pass the result to `prepareReportInput`. */
export const useReportInput = (): ReportInput =>
  useAppStore(useShallow(selectReportInput));

/** Some retained detection result no longer describes the current inputs. */
export const selectDetectionStale = (
  state: Pick<
    AppStore,
    | 'blunderResults'
    | 'dataSnoopingResults'
    | 'combinatorialStale'
    | 'dataSnoopingStale'
  >
): boolean =>
  (state.combinatorialStale && !!state.blunderResults) ||
  (state.dataSnoopingStale && !!state.dataSnoopingResults);

/** Every pair flagged by either detection method, in verdict order. */
export const getFlaggedPointIds = (verdict: BlunderVerdict): number[] => [
  ...new Set(
    [
      ...verdict.agreed,
      ...verdict.combinatorialOnly,
      ...verdict.dataSnoopingOnly,
      ...(verdict.state === 'single'
        ? (verdict.combinatorial ?? verdict.dataSnooping ?? [])
        : []),
    ].map((point) => point.id)
  ),
];

export interface BlunderVerdictView {
  verdict: BlunderVerdict;
  /** Pair ids flagged by the verdict (see `getFlaggedPointIds`). */
  flaggedPointIds: number[];
  /** Some retained detection result is outdated. */
  detectionStale: boolean;
}

const selectVerdictInput = (state: AppStore) => ({
  blunderResults: state.blunderResults,
  dataSnoopingResults: state.dataSnoopingResults,
  combinatorialStale: state.combinatorialStale,
  dataSnoopingStale: state.dataSnoopingStale,
  pointPairs: state.pointPairs,
});

/**
 * The cross-method blunder verdict, derived once per change.
 *
 * By default outdated results are excluded, so flags only come from current
 * detection methods (point table, map, workflow status). Pass
 * `{ includeStale: true }` to describe retained outdated results, e.g. in a
 * review panel that shows them behind a freshness warning.
 */
export const useBlunderVerdict = ({
  includeStale = false,
}: { includeStale?: boolean } = {}): BlunderVerdictView => {
  const {
    blunderResults,
    dataSnoopingResults,
    combinatorialStale,
    dataSnoopingStale,
    pointPairs,
  } = useAppStore(useShallow(selectVerdictInput));
  return useMemo(() => {
    const verdict = deriveBlunderVerdict(
      includeStale || !combinatorialStale ? blunderResults : null,
      includeStale || !dataSnoopingStale ? dataSnoopingResults : null,
      pointPairs
    );
    return {
      verdict,
      flaggedPointIds: getFlaggedPointIds(verdict),
      detectionStale: selectDetectionStale({
        blunderResults,
        dataSnoopingResults,
        combinatorialStale,
        dataSnoopingStale,
      }),
    };
  }, [
    includeStale,
    blunderResults,
    dataSnoopingResults,
    combinatorialStale,
    dataSnoopingStale,
    pointPairs,
  ]);
};
